import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Plus, Edit3, Trash2 } from 'lucide-react';
import { useTaskComments, TaskComment } from '@/hooks/useTaskComments';
import { format } from 'date-fns';
import { EditCommentDialog } from './EditCommentDialog';
import { supabase } from '@/integrations/supabase/client';

interface CommentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  taskType: 'foundational' | 'growth' | 'recurring';
  onAddComment: () => void;
  taskBrandId?: string;
}

const CommentItem = ({ comment, onEdit, onDelete, currentUserId }: { 
  comment: TaskComment; 
  onEdit: () => void; 
  onDelete: () => void;
  currentUserId: string | null;
}) => {
  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary/80"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const isOwner = currentUserId && comment.user_id === currentUserId;
  
  // Format author display name
  const authorDisplay = comment.author_name && comment.author_alias 
    ? `${comment.author_name} (${comment.author_alias})`
    : comment.author_name || comment.author_alias || 'Unknown User';

  return (
    <div className="space-y-2 p-3 border rounded-lg bg-background/50">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">{authorDisplay}</span>
          <span className="text-xs text-muted-foreground">
            {format(new Date(comment.created_at), 'MMM dd, yyyy \'at\' h:mm a')}
          </span>
        </div>
        {isOwner && (
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onEdit}
              className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
            >
              <Edit3 className="h-3 w-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDelete}
              className="h-6 w-6 p-0 opacity-70 hover:opacity-100 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      <div className="text-sm text-foreground whitespace-pre-wrap">
        {renderTextWithLinks(comment.comment_text)}
      </div>
    </div>
  );
};

export const CommentsDialog: React.FC<CommentsDialogProps> = ({
  isOpen,
  onClose,
  taskId,
  taskType,
  onAddComment,
  taskBrandId,
}) => {
  const { comments, isLoading, deleteComment } = useTaskComments(taskId, taskType, taskBrandId);
  const [editingComment, setEditingComment] = useState<TaskComment | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<TaskComment | null>(null);

  React.useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  const handleDeleteComment = async (comment: TaskComment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (commentToDelete) {
      try {
        await deleteComment(commentToDelete.id);
        setDeleteDialogOpen(false);
        setCommentToDelete(null);
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitleWithBug 
              popupName="Task Comments" 
              popupContext="/my-tasks"
              isOpen={isOpen}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  User's comments ({comments.length})
                </div>
              </div>
            </DialogTitleWithBug>
          </DialogHeader>

          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={onAddComment}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add New
            </Button>
          </div>

          <ScrollArea className="max-h-[60vh] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">Loading comments...</div>
              </div>
            ) : comments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">No comments yet</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Be the first to add a comment
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <CommentItem 
                    key={comment.id} 
                    comment={comment} 
                    onEdit={() => setEditingComment(comment)}
                    onDelete={() => handleDeleteComment(comment)}
                    currentUserId={currentUserId}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>

        {editingComment && (
          <EditCommentDialog
            isOpen={!!editingComment}
            onClose={() => setEditingComment(null)}
            taskId={taskId}
            taskType={taskType}
            commentId={editingComment.id}
            initialText={editingComment.comment_text}
            onSuccess={() => setEditingComment(null)}
          />
        )}
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[300px] [&>[data-radix-dialog-close]]:hidden">
          <DialogHeader>
            <h2 className="text-lg font-semibold text-center">Delete this comment</h2>
          </DialogHeader>
          <div className="text-center py-2">
            <p className="text-sm text-muted-foreground mb-4">This action cannot be undone.</p>
            <DialogFooter className="flex justify-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};